import {
  CheckIcon,
  Paragraph,
  PrimaryButton,
  Spinner,
  Tooltip,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { t } from 'i18next';
import { useState } from 'react';
import { type GetWebcastSummaryManagerQuery, useEnableVodMutation } from 'src/generated/graphql-manager';
import { getLanguageKey } from 'src/utils';

import { EnableVodPopup } from '../enabled-vod-popup/enable-vod-popup';
import { EventSummaryRecording } from '../event-summary-recording/event-summary-recording';
import { EventSummaryVod } from '../event-summary-vod/event-summary-vod';

import styles from './event-summary-video.module.css';

type EventSummaryVideoProps = {
  webcast: GetWebcastSummaryManagerQuery['webcast'];
  onVideoSelect: () => void;
};

export function EventSummaryVideo({ webcast, onVideoSelect }: EventSummaryVideoProps) {
  const [showEnableVodPopup, setShowEnableVodPopup] = useState(false);

  const [enableVodMutation, { loading: enablingVod }] = useEnableVodMutation();

  const webcastId = webcast.id;
  const primaryLanguage = webcast?.primaryLang;
  const additionalLanguages = webcast?.additionalLangs || [];
  const languages = [primaryLanguage, ...additionalLanguages];
  const streaming = webcast?.streaming;
  const vodVideos = streaming?.vod?.streams;
  const vodEnabled = streaming?.vod?.enabled;
  const areVideosChoosen =
    vodVideos?.length === languages.length && vodVideos.every((vodVideo) => vodVideo.reference.videoId);

  const multipleLanguages = languages.length > 1;
  const recordingEnabled = webcast?.recordingSettings?.enabled;

  const handleEnableVod = () => {
    setShowEnableVodPopup(false);

    enableVodMutation({
      variables: { input: { webcastId } },
      onCompleted: onVideoSelect,
    });
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          {(multipleLanguages || recordingEnabled) && (
            <div
              className={classNames(multipleLanguages && recordingEnabled ? styles.leftGroup : '')}
              style={{ gap: '20px' }}
            >
              {multipleLanguages && (
                <div data-testid="languages-header" className={styles.col}>
                  {t('manager.eventSummary.languages')}
                </div>
              )}
              {recordingEnabled && (
                <div data-testid="recording-header">{t('manager.eventSummary.recording.title')}</div>
              )}
            </div>
          )}
          <div data-testid="vod-header">{t('manager.eventSummary.videoOnDemand.title')}</div>{' '}
        </div>

        {languages.map((language) => (
          <div className={styles.row} key={language.code}>
            {(multipleLanguages || recordingEnabled) && (
              <div className={classNames(multipleLanguages && recordingEnabled ? styles.leftGroup : '')}>
                {multipleLanguages && (
                  <div className={classNames(styles.ellipsis, styles.language)}>
                    <Paragraph data-testid={`event-summary-language-${language.code}`}>
                      {language.__typename === 'CustomLanguage'
                        ? language.name
                        : t(`common.languages.${getLanguageKey(language.code)}`)}
                    </Paragraph>
                  </div>
                )}
                {recordingEnabled && (
                  <div className={styles.ellipsis}>
                    <EventSummaryRecording webcast={webcast} language={language.code} />
                  </div>
                )}
              </div>
            )}
            <div
              data-testid={`vod-container-${language.code}`}
              className={classNames(styles.ellipsis, styles.onDemand)}
            >
              <EventSummaryVod webcast={webcast} language={language.code} onVideoSelect={onVideoSelect} />
            </div>
          </div>
        ))}

        {!vodEnabled ? (
          <Tooltip
            label={
              languages.length > 1
                ? t('manager.eventSummary.videoOnDemand.switchToOnDemandButtonTooltip.multiLanguage')
                : t('manager.eventSummary.videoOnDemand.switchToOnDemandButtonTooltip.singleLanguage')
            }
            hidden={areVideosChoosen}
          >
            <PrimaryButton
              data-testid="enable-vod-button"
              small
              disabled={!areVideosChoosen || enablingVod}
              onClick={() => setShowEnableVodPopup(true)}
              className={styles.onDemandButton}
            >
              {enablingVod ? <Spinner size={18} /> : t('manager.eventSummary.videoOnDemand.switchToOnDemandButton')}
            </PrimaryButton>
          </Tooltip>
        ) : (
          <div data-testid="vod-enabled" className={styles.vodEnabled}>
            {t('manager.eventSummary.videoOnDemand.onDemandSwitchedOn')} <CheckIcon />
          </div>
        )}
      </div>

      <EnableVodPopup
        isOpen={showEnableVodPopup}
        onClose={() => setShowEnableVodPopup(false)}
        onSwitchToOnDemand={handleEnableVod}
      />
    </>
  );
}
